import React, { useContext } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { postRequest } from '../../api/ApiController';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { getSemaforColorOptions, SEMAFOR_COLOR_VALUES, getSemaforColorClass } from '../../utils/moduleSettings';

const TICKET_INDICATOR_KEYS = [
  { key: 'days_to_deadline', labelKey: 'CustomerGeneralSettings.TicketIndicator.DaysToDeadline', fallback: '5', max: 99 },
  { key: 'undefined_state_deadline_days', labelKey: 'CustomerGeneralSettings.TicketIndicator.UndefinedStateDeadline', fallback: '5', max: 99 },
];

const CUSTOMER_LIST_DEADLINE_KEYS = [
  { key: 'customer_deadline_warning_days', labelKey: 'CustomerGeneralSettings.CustomerDeadlineWarningDays', fallback: '5', max: 365 },
];

const TOGGLE_VISIBILITY_KEYS = [
  { key: 'show_customer_lost_in_lists', labelKey: 'CustomerGeneralSettings.ShowLostCustomers', fallback: '1' },
  { key: 'show_customer_potential_in_lists', labelKey: 'CustomerGeneralSettings.ShowPotentialCustomers', fallback: '1' },
];

const DEADLINE_DOT_COLOR_KEYS = [
  { key: 'status_indicator_red', labelKey: 'CustomerGeneralSettings.DeadlineDotColor.Overdue', fallback: 'red' },
  { key: 'status_indicator_orange', labelKey: 'CustomerGeneralSettings.DeadlineDotColor.Warning', fallback: 'orange' },
  { key: 'status_indicator_green', labelKey: 'CustomerGeneralSettings.DeadlineDotColor.Ok', fallback: 'green' },
];

const PORTFOLIO_COLOR_KEYS = [
  { key: 'portfolio_new_color', labelKey: 'CustomerGeneralSettings.Portfolio.New', fallback: 'gray' },
  { key: 'portfolio_potential_color', labelKey: 'CustomerGeneralSettings.Portfolio.Potential', fallback: 'black' },
  { key: 'portfolio_verified_color', labelKey: 'CustomerGeneralSettings.Portfolio.Verified', fallback: 'purple' },
  { key: 'portfolio_inquiring_color', labelKey: 'CustomerGeneralSettings.Portfolio.Inquiring', fallback: 'blue' },
  { key: 'portfolio_occasional_color', labelKey: 'CustomerGeneralSettings.Portfolio.Occasional', fallback: 'yellow' },
  { key: 'portfolio_regular_color', labelKey: 'CustomerGeneralSettings.Portfolio.Regular', fallback: 'orange' },
  { key: 'portfolio_strategic_color', labelKey: 'CustomerGeneralSettings.Portfolio.Strategic', fallback: 'green' },
  { key: 'portfolio_lost_color', labelKey: 'CustomerGeneralSettings.Portfolio.Lost', fallback: 'red' },
];

const OTHER_GRAPH_COLOR_KEYS = [
  { key: 'turnover_occasional_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.TurnoverOccasional', fallback: 'yellow' },
  { key: 'turnover_regular_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.TurnoverRegular', fallback: 'orange' },
  { key: 'turnover_strategic_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.TurnoverStrategic', fallback: 'green' },
  { key: 'turnover_lost_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.TurnoverLost', fallback: 'red' },
  { key: 'acquisition_unsuccessful_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.AcquisitionUnsuccessful', fallback: 'red' },
  { key: 'acquisition_running_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.AcquisitionRunning', fallback: 'orange' },
  { key: 'acquisition_successful_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.AcquisitionSuccessful', fallback: 'green' },
  { key: 'inquiry_eval_unsuccessful_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryEvalUnsuccessful', fallback: 'red' },
  { key: 'inquiry_eval_rejected_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryEvalRejected', fallback: 'orange' },
  { key: 'inquiry_eval_successful_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryEvalSuccessful', fallback: 'green' },
  { key: 'inquiry_state_rejections_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryStateRejections', fallback: 'gray' },
  { key: 'inquiry_state_inquiries_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryStateInquiries', fallback: 'blue' },
  { key: 'inquiry_state_offers_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryStateOffers', fallback: 'green' },
  { key: 'inquiry_state_decision_color', labelKey: 'CustomerGeneralSettings.OtherGraphs.InquiryStateDecision', fallback: 'orange' },
];

function getSetting(moduleSettings, key, fallback) {
  const row = (moduleSettings || []).find((s) => s.module === 'crm' && s.key === key);
  if (!row || row.value == null || row.value === '') return fallback;
  return String(row.value);
}

function getBooleanSetting(moduleSettings, key, fallback = '1') {
  const v = getSetting(moduleSettings, key, fallback);
  return v === '0' ? '0' : '1';
}

function getNumericSetting(moduleSettings, key, fallback = '5') {
  const raw = getSetting(moduleSettings, key, fallback);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : Number(fallback);
}

function getColorSetting(moduleSettings, key, fallback = 'orange') {
  const v = getSetting(moduleSettings, key, fallback);
  return SEMAFOR_COLOR_VALUES.includes(v) ? v : fallback;
}

const COMMUNICATION_APPROVAL_RULE_TYPES = Object.freeze([
  { key: 'announcement', labelKey: 'CrmCommunication.Type.announcement' },
  { key: 'acquisition', labelKey: 'CrmCommunication.Type.acquisition' },
  { key: 'quote_request', labelKey: 'CrmCommunication.Type.quote_request' },
]);

const DEFAULT_COMMUNICATION_APPROVAL_RULES = Object.freeze({
  announcement: { required: true, max_levels: 0 },
  acquisition: { required: true, max_levels: 0 },
  quote_request: { required: true, max_levels: 0 },
});

function getCommunicationApprovalRules(moduleSettings) {
  const row = (moduleSettings || []).find((s) => s.module === 'crm' && s.key === 'communication_approval_rules');
  if (!row?.value) {
    return { ...DEFAULT_COMMUNICATION_APPROVAL_RULES };
  }
  try {
    const parsed = JSON.parse(String(row.value));
    return {
      announcement: { ...DEFAULT_COMMUNICATION_APPROVAL_RULES.announcement, ...(parsed?.announcement || {}) },
      acquisition: { ...DEFAULT_COMMUNICATION_APPROVAL_RULES.acquisition, ...(parsed?.acquisition || {}) },
      quote_request: { ...DEFAULT_COMMUNICATION_APPROVAL_RULES.quote_request, ...(parsed?.quote_request || {}) },
    };
  } catch (_) {
    return { ...DEFAULT_COMMUNICATION_APPROVAL_RULES };
  }
}

export default function CustomerGeneralSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const moduleSettings = state?.module_settings || [];
  const toggleOptions = [
    { label: t('Common.No'), value: '0' },
    { label: t('Common.Yes'), value: '1' },
  ];
  const colorOptions = getSemaforColorOptions(t);

  async function saveValue(def, value, currentValue) {
    const row = moduleSettings.find((s) => s.module === 'crm' && s.key === def.key);
    if (!row) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        life: 5000,
        detail: t('ServiceGeneralSettings.SettingNotFound'),
      });
      return;
    }
    const normalized = value == null ? '' : String(value).trim();
    if (normalized === String(currentValue ?? '')) return;
    try {
      await postRequest(`/api/module_settings/update/${row.id}`, { value: normalized }, true);
      dispatch({ type: 'update_module_setting', id: row.id, value: normalized });
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceTask.SaveFailed') });
    }
  }

  async function saveBoolean(def, value) {
    const normalized = value === '0' ? '0' : '1';
    const current = getBooleanSetting(moduleSettings, def.key, def.fallback);
    await saveValue(def, normalized, current);
  }

  async function saveNumber(def, value) {
    const normalized = String(Math.max(1, Math.min(365, Number(value || def.fallback))));
    const current = String(getNumericSetting(moduleSettings, def.key, def.fallback));
    await saveValue(def, normalized, current);
  }

  async function saveColor(def, value) {
    if (!SEMAFOR_COLOR_VALUES.includes(value)) return;
    const current = getColorSetting(moduleSettings, def.key, def.fallback);
    if (value === current) return;
    await saveValue(def, value, current);
  }

  const approvalRulesRow = moduleSettings.find((s) => s.module === 'crm' && s.key === 'communication_approval_rules');
  const approvalRules = getCommunicationApprovalRules(moduleSettings);

  async function saveApprovalRuleRequired(typeKey, required) {
    if (!approvalRulesRow) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        detail: t('ServiceGeneralSettings.SettingNotFound'),
        life: 4000,
      });
      return;
    }
    const next = {
      ...approvalRules,
      [typeKey]: {
        ...approvalRules[typeKey],
        required: required === '1',
      },
    };
    const normalized = JSON.stringify(next);
    if (normalized === String(approvalRulesRow.value ?? '')) return;
    try {
      await postRequest(`/api/module_settings/update/${approvalRulesRow.id}`, { value: normalized }, true);
      dispatch({ type: 'update_module_setting', id: approvalRulesRow.id, value: normalized });
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceTask.SaveFailed') });
    }
  }

  function ColorRow({ def }) {
    const currentValue = getColorSetting(moduleSettings, def.key, def.fallback);
    const colorClass = getSemaforColorClass(currentValue);
    return (
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(220px,1fr)_220px] gap-3 items-center py-1.5">
        <label className="text-sm font-medium text-gray-800 truncate">{t(def.labelKey)}</label>
        <div className="flex items-center gap-2 shrink-0 min-w-0">
          <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
          <Dropdown
            value={currentValue}
            options={colorOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => saveColor(def, e.value)}
            className="w-[180px] border border-gray-400 rounded"
            placeholder={t('ServiceGeneralSettings.Color')}
          />
        </div>
      </div>
    );
  }

  function NumberRow({ def, max = def.max ?? 365 }) {
    const currentValue = getNumericSetting(moduleSettings, def.key, def.fallback);
    return (
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
        <label className="text-sm font-medium text-gray-800">{t(def.labelKey)}</label>
        <InputNumber
          value={currentValue}
          onValueChange={(e) => {
            const next = Math.max(1, Math.min(max, Number(e.value || currentValue)));
            saveNumber(def, next);
          }}
          min={1}
          max={max}
          showButtons
          className="border border-gray-300 rounded-md"
          size={6}
          useGrouping={false}
        />
      </div>
    );
  }

  function ToggleRow({ def }) {
    const currentValue = getBooleanSetting(moduleSettings, def.key, def.fallback);
    return (
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(220px,1fr)_auto] gap-3 items-center">
        <label className="text-sm font-medium text-gray-800">{t(def.labelKey)}</label>
        <div className="flex items-center gap-2 shrink-0">
          <SelectButton
            value={currentValue}
            options={toggleOptions}
            optionLabel="label"
            optionValue="value"
            allowEmpty={false}
            onChange={(e) => saveBoolean(def, e.value)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">
          {t('CustomerGeneralSettings.Title')}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-stretch">
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('CustomerGeneralSettings.TicketIndicator.Title')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('CustomerGeneralSettings.TicketIndicator.Help')}
            </p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-2">
              {TICKET_INDICATOR_KEYS.map((def) => (
                <NumberRow key={def.key} def={def} max={def.max} />
              ))}
            </div>
            <p className="text-sm text-gray-500 mb-6 pl-4 border-l-2 border-gray-200">
              {t('CustomerGeneralSettings.TicketIndicator.UndefinedStateDeadlineHint')}
            </p>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">
              {t('CustomerGeneralSettings.DeadlineDot.Title')}
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              {t('CustomerGeneralSettings.DeadlineDot.Help')}
            </p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200 mb-6">
              {DEADLINE_DOT_COLOR_KEYS.map((def) => (
                <ColorRow key={def.key} def={def} />
              ))}
            </div>
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">
              {t('CustomerGeneralSettings.CustomerListDeadline.Title')}
            </h2>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              {CUSTOMER_LIST_DEADLINE_KEYS.map((def) => (
                <NumberRow key={def.key} def={def} max={def.max} />
              ))}
            </div>
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">
              {t('CustomerGeneralSettings.ListVisibility.Title')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('CustomerGeneralSettings.ListVisibility.Help')}
            </p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              {TOGGLE_VISIBILITY_KEYS.map((def) => (
                <ToggleRow key={def.key} def={def} />
              ))}
            </div>
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">
              {t('CrmCommunication.Settings.ApprovalRules.Title')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('CrmCommunication.Settings.ApprovalRules.Help')}
            </p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              {COMMUNICATION_APPROVAL_RULE_TYPES.map((def) => {
                const required = approvalRules[def.key]?.required !== false ? '1' : '0';
                return (
                  <div key={def.key} className="grid grid-cols-1 sm:grid-cols-[minmax(220px,1fr)_auto] gap-3 items-center">
                    <label className="text-sm font-medium text-gray-800">{t(def.labelKey)}</label>
                    <SelectButton
                      value={required}
                      options={toggleOptions}
                      optionLabel="label"
                      optionValue="value"
                      allowEmpty={false}
                      onChange={(e) => void saveApprovalRuleRequired(def.key, e.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('CustomerGeneralSettings.PortfolioGraph.Title')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('CustomerGeneralSettings.PortfolioGraph.Help')}
            </p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {PORTFOLIO_COLOR_KEYS.map((def) => (
                <ColorRow key={def.key} def={def} />
              ))}
            </div>
          </div>

          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              {t('CustomerGeneralSettings.OtherGraphs.Title')}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t('CustomerGeneralSettings.OtherGraphs.Help')}
            </p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {OTHER_GRAPH_COLOR_KEYS.map((def) => (
                <ColorRow key={def.key} def={def} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
